import { z } from "zod";
import {
  TimesheetTransmissionTypeSchema,
  DefinitiveEndTypeSchema,
  ContractStatusSchema,
  AmendmentTypeSchema,
  EmployeeStatusSchema,
  ContractReasonSchema,
  JobRiskSchema,
  MealTypeSchema,
  PpeSchema,
  SirhSchema,
  DayOfWeekSchema,
} from "../../types/supabase.types.gen";
import { zodDateSchema, zodNullableTimeHHmmssSchema } from "../shared";
import { remunerationProfileInsertPayloadSchema } from "../remuneration/remuneration-profile";

/**
 * POST /contracts body: required relation UUIDs, optional contract row scalars, optional nested remuneration.
 */
export const contractMutationBodySchema = z.object({
  agency_id: z.guid(),
  mission_id: z.guid(),
  owner_id: z.guid(),
  talent_id: z.guid(),
  workspace_id: z.guid(),

  should_fill_timesheets_on_app: z.boolean().optional(),
  timesheet_transmission_type: TimesheetTransmissionTypeSchema.nullable().optional(),
  coefficient: z.number().optional(),
  hourly_wage: z.number().optional(),
  is_sirh_request: z.boolean().optional(),
  amendment_type: AmendmentTypeSchema.nullable().optional(),
  start_date: zodDateSchema.nullable().optional(),
  end_date: zodDateSchema.nullable().optional(),
  real_end_date: zodDateSchema.nullable().optional(),
  definitive_end_type: DefinitiveEndTypeSchema.nullable().optional(),
  status: ContractStatusSchema.optional(),
  is_signature_on_worksite_possible: z.boolean().nullable().optional(),
  organization_id: z.string().nullable().optional(),

  address_id: z.guid().nullable().optional(),
  admin_id: z.guid().nullable().optional(),
  amendment_comment: z.string().optional(),
  compensation_bonus_others: z.string().optional(),
  compensation_bonus_with_charges: z.string().optional(),
  compensation_bonus_without_charges: z.string().optional(),
  contract_reason: ContractReasonSchema.nullable().optional(),
  created_at: zodDateSchema.optional(),
  definitive_end_created_at: zodDateSchema.nullable().optional(),
  definitive_end_details: z.string().optional(),
  definitive_end_processed_at: zodDateSchema.nullable().optional(),
  definitive_end_type_v2: DefinitiveEndTypeSchema.nullable().optional(),
  employee_status: EmployeeStatusSchema.nullable().optional(),
  is_pre_employment_declaration_needed: z.boolean().optional(),
  is_professional_card_required: z.boolean().nullable().optional(),
  is_talent_onboarding_needed: z.boolean().nullable().optional(),
  is_termination_bonus_paid_monthly: z.boolean().optional(),
  is_termination_bonus_to_be_paid: z.boolean().nullable().optional(),
  job_description: z.string().optional(),
  job_id: z.guid().nullable().optional(),
  job_name: z.string().optional(),
  job_risks: z.array(JobRiskSchema).optional(),
  location: z.unknown().optional(),
  meal_card_value: z.number().nullable().optional(),
  meal_type: MealTypeSchema.optional(),
  mission_statement_description: z.string().optional(),
  overtime: z.string().optional(),
  ppe: z.array(PpeSchema).optional(),
  ppe_comments: z.string().optional(),
  previous_contract_key: z.number().nullable().optional(),
  /** FK to a contract-owned profile. Ignored when `remuneration_profile` nested payload is sent. */
  remuneration_profile_id: z.guid().nullable().optional(),
  /**
   * Nested profile snapshot (same shape as company templates). When present, the API creates a new
   * `remuneration_profile` row, sets the FK, and dual-writes legacy remu columns.
   */
  remuneration_profile: remunerationProfileInsertPayloadSchema.optional(),
  risk_others: z.string().optional(),
  send_timesheet_first_to_client: z.boolean().nullable().optional(),
  sirh_name: SirhSchema.nullable().optional(),
  summary_sheet_id: z.guid().nullable().optional(),
  timesheet_launch_date: zodDateSchema.nullable().optional(),
  updated_at: zodDateSchema.nullable().optional(),
  working_days: z.array(DayOfWeekSchema).nullable().optional(),
  working_hours: z.string().optional(),
  weekly_hours: z.number().positive().nullable().optional(),
  overtime_weekly_hours: z.number().nonnegative().nullable().optional(),
  morning_start_time: zodNullableTimeHHmmssSchema.optional(),
  morning_end_time: zodNullableTimeHHmmssSchema.optional(),
  afternoon_start_time: zodNullableTimeHHmmssSchema.optional(),
  afternoon_end_time: zodNullableTimeHHmmssSchema.optional(),
  working_hours_comment: z.string().nullable().optional(),
});

/**
 * `address_extra_info` is saved on the contract's work address. `close_mission` marks the contract's
 * mission as completed.
 */
export const postContractBodySchema = contractMutationBodySchema.extend({
  renewed_from_contract_key: z.number().optional(),
  address_extra_info: z.string().nullable().optional(),
  close_mission: z.boolean().optional(),
});

export const isValidDateRange = (data: { start_date?: string | null; end_date?: string | null }) =>
  !data.start_date || !data.end_date || data.end_date >= data.start_date;
export const invalidDateRangeError = {
  message: "La date de fin ne peut pas être antérieure à la date de début",
  path: ["end_date"],
};

export const postContractResponseSchema = z.object({
  id: z.guid(),
  contract_key: z.number(),
});

export type PostContractBody = z.infer<typeof postContractBodySchema>;
export type PostContractResponse = z.infer<typeof postContractResponseSchema>;
